import React from 'react'
import { Badge, Button, Menu, MenuProps, Popover } from 'antd'
import {
  DownOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import classNames from 'classnames'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import { chatApi } from 'api/chat'
import { sellerApi } from 'api/seller'
import { showMessage } from 'shared/utils/showMessage'
import { AxiosError } from 'axios'
import { adminRoutes, superAdminRoutes } from 'shared/constants/routes'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useIsCompactMenu } from 'shared/hooks/useIsCompactMenu'
import { useLogoutConfirm } from 'shared/hooks/useLogoutConfirm'
import { useSellerAccess } from 'shared/hooks/useSellerAccess'
import { useSideMenuCollapse } from 'shared/hooks/useSideMenuCollapse'
import { useWideLayout } from 'shared/hooks/useWideLayout'
import { chatSelectors } from 'store/chat'
import styles from './SellerWrapper.module.scss'

const LOGOUT_KEY = 'logout'

type TMenuItem = NonNullable<MenuProps['items']>[number]
// Уже этой ширины меню встаёт над разделом, а не сбоку, — сворачивать его некуда.
// Та же граница, что в SellerWrapper.module.scss
const STACKED_MENU_MAX_WIDTH = 1120
// Курсор, пересекающий колонку по пути к разделу, не должен раскрывать меню
const PEEK_DELAY_MS = 120
// Ширина широких таблиц раздела. Рядом со свёрнутым меню (70 + отступ 32) они встают
// без прокрутки с окна 1114px: 972 + 102 + поля страницы 40
export const SELLER_SECTION_WIDTH = 972
// В широкой раскладке уже этого окна широкая таблица не помещается рядом с раскрытым меню:
// 972 + 256 (меню) + 62 (отступ) + 40 (поля страницы) + 17 (полоса прокрутки) − 1
const CRAMPED_MENU_MAX_WIDTH = 1346

interface IAnchorsWrapperProps {
  type: 'seller' | 'profile'
  links: MenuProps['items']
  // Разделы, при входе в которые меню сворачивается само: им нужна вся ширина
  autoCollapseKeys?: string[]
}

const getCurrentKey = (type: IAnchorsWrapperProps['type'], pathname: string) =>
  (type === 'seller' ? pathname.split('/')[3] : pathname.split('/')[2]) ?? ''

export const SellerWrapper: React.FC<IAnchorsWrapperProps> = ({
  type,
  links,
  autoCollapseKeys
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const unreadChatCount = useAppSelector(chatSelectors.selectUnreadCount)
  const isMobile = useIsMobile()
  const isCompactMenu = useIsCompactMenu()
  const isStackedMenu = useIsMobile(STACKED_MENU_MAX_WIDTH)
  const isCrampedMenu = useIsMobile(CRAMPED_MENU_MAX_WIDTH)
  const { isWide } = useWideLayout()
  const logoutConfirm = useLogoutConfirm()
  const { canSeeSection } = useSellerAccess()

  const { id } = useParams<{ id: string }>()
  const [sellerNumberId, setSellerNumberId] = React.useState<null | number>(null)
  const [sellerName, setSellerName] = React.useState<string | null>(null)
  const [sellerUnreadChatCount, setSellerUnreadChatCount] = React.useState(0)
  // Сразу из адреса, а не пустой строкой: иначе раздел, сворачивающий меню,
  // при открытии по ссылке показал бы, как оно сворачивается
  const [current, setCurrent] = React.useState(() => getCurrentKey(type, location.pathname))
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const [menuWidth, setMenuWidth] = React.useState<number>()
  const [openKeys, setOpenKeys] = React.useState<string[]>([])
  const sectionButtonRef = React.useRef<HTMLButtonElement>(null)
  const [isPeeking, setIsPeeking] = React.useState(false)
  const peekTimerRef = React.useRef<number>()

  // Рядом с раскрытым меню раздел помещается только в широкой раскладке —
  // в узкой (1074px) места нет при любом окне
  const { isCollapsed, toggleCollapsed } = useSideMenuCollapse(
    current,
    autoCollapseKeys,
    isWide && !isCrampedMenu
  )
  const isCollapsible = !isCompactMenu && !isStackedMenu
  const isSideCollapsed = isCollapsible && isCollapsed
  // Свёрнуто и не раскрыто наведением — видны только иконки
  const isFolded = isSideCollapsed && !isPeeking

  React.useEffect(() => {
    if (type === 'seller') {
      const getSeller = async () => {
        try {
          const { data } = await sellerApi.getSeller(id as string)
          setSellerNumberId(data.number)
          setSellerName(data.shop_details?.shop_name ?? null)
        } catch (e) {
          const error = e as AxiosError
          if (error.response?.status === 500 || error.response?.status === 400 || error.response?.status === 404) {
            return
          }
          showMessage('error', 'Не удалось получить ID продавца', '')
        }
      }
      getSeller()
    }
  }, [])

  // Админ чаты продавца не прочитывает — счётчик меняет только продавец,
  // поэтому сверяемся с сервером при каждом переходе по карточке
  React.useEffect(() => {
    if (type !== 'seller' || !id) return undefined

    let isActual = true
    chatApi
      .getAdminSellerUnreadCount(id)
      .then(({ data }) => {
        if (isActual) setSellerUnreadChatCount(data.unreadCount)
      })
      .catch(() => undefined)

    return () => {
      isActual = false
    }
  }, [type, id, location.pathname])

  React.useEffect(() => {
    setCurrent(getCurrentKey(type, location.pathname))
  }, [location.pathname])

  // Меню только что свернули, а курсор ещё над ним — не раскрываем его обратно,
  // пока курсор не уйдёт и не вернётся
  React.useEffect(() => {
    window.clearTimeout(peekTimerRef.current)
    setIsPeeking(false)
  }, [isSideCollapsed])

  React.useEffect(() => () => window.clearTimeout(peekTimerRef.current), [])

  const onSideMouseEnter = () => {
    if (!isSideCollapsed) return
    window.clearTimeout(peekTimerRef.current)
    peekTimerRef.current = window.setTimeout(() => setIsPeeking(true), PEEK_DELAY_MS)
  }

  const onSideMouseLeave = () => {
    window.clearTimeout(peekTimerRef.current)
    setIsPeeking(false)
  }

  const onClick: MenuProps['onClick'] = (e) => {
    setIsMenuOpen(false)
    if (e.key === LOGOUT_KEY) {
      return logoutConfirm()
    }
    setCurrent(e.key)
    navigate(e.key)
  }

  const menuLinks = React.useMemo<MenuProps['items']>(() => {
    const chatsKey = type === 'profile' ? adminRoutes.PROFILE_CHATS : superAdminRoutes.SELLER_CHATS
    const chatsUnreadCount = type === 'profile' ? unreadChatCount : sellerUnreadChatCount

    const withChatsBadge = (items: MenuProps['items']) => items?.map((link) => {
      if (!link || !('key' in link) || !('label' in link) || link.key !== chatsKey) return link

      return {
        ...link,
        // подпись со счётчиком в свёрнутом меню не видна — непрочитанное отмечаем на иконке
        ...(isFolded && chatsUnreadCount > 0 && 'icon' in link
          ? { icon: <Badge dot>{link.icon}</Badge> }
          : {}),
        label: (
          <span>
            {link.label}
            <Badge
              count={chatsUnreadCount}
              size='small'
              offset={[14, -1]}
              className={styles.chatsBadge}
            />
          </span>
        )
      }
    })

    if (type !== 'profile') return withChatsBadge(links)

    const isVisible = (item: TMenuItem) =>
      !item || !('key' in item) || canSeeSection(`${adminRoutes.PROFILE}/${String(item.key)}`)

    // Сотруднику — только разделы из его прав; группа без доступных подпунктов пропадает
    const accessibleLinks = links?.reduce<TMenuItem[]>((acc, item) => {
      if (item && 'children' in item && item.children) {
        const children = item.children.filter(isVisible)
        if (children.length) acc.push({ ...item, children })
      } else if (isVisible(item)) {
        acc.push(item)
      }
      return acc
    }, [])

    const linksWithBadge = withChatsBadge(accessibleLinks)

    // В шапке выхода нет (там «Поддержка»), поэтому он живёт в меню профиля.
    // Рядом с таббаром из него убраны сообщения — у них своя вкладка
    const visibleLinks = isMobile
      ? linksWithBadge?.filter(
        (link) => !link || !('key' in link) || link.key !== adminRoutes.PROFILE_CHATS
      )
      : linksWithBadge

    return [
      ...(visibleLinks ?? []),
      {
        key: LOGOUT_KEY,
        label: 'Выйти',
        icon: <LogoutOutlined />,
        danger: true
      }
    ]
  }, [links, type, unreadChatCount, sellerUnreadChatCount, isMobile, isFolded, canSeeSection])

  // Подпись кнопки на телефоне — название открытого раздела, в том числе вложенного
  const currentLabel = React.useMemo(() => {
    const findLabel = (items: MenuProps['items']): React.ReactNode => {
      for (const item of items ?? []) {
        if (!item || !('key' in item)) continue
        if (item.key === current && 'label' in item) return item.label
        if ('children' in item) {
          const nested = findLabel(item.children as MenuProps['items'])
          if (nested) return nested
        }
      }
      return undefined
    }

    return findLabel(menuLinks) ?? 'Разделы'
  }, [menuLinks, current])

  // Раздел с подпунктами раскрыт, если открыт один из них
  const openedSubmenuKeys = React.useMemo(() => {
    const parent = menuLinks?.find(
      (item) =>
        item &&
        'children' in item &&
        (item.children as MenuProps['items'])?.some(
          (child) => child && 'key' in child && child.key === current
        )
    )

    return parent && 'key' in parent ? [String(parent.key)] : []
  }, [menuLinks, current])

  const onMenuOpenChange = (open: boolean) => {
    if (open) {
      // меню живёт в портале и своей ширины не знает — берём её у кнопки
      setMenuWidth(sectionButtonRef.current?.offsetWidth)
      setOpenKeys(openedSubmenuKeys)
    }
    setIsMenuOpen(open)
  }

  const sectionsMenu = isCompactMenu ? (
    // Popover, а не Dropdown: Dropdown навязывает вложенному меню
    // mode="vertical", и подпункты открываются всплывашкой за краем экрана.
    // autoAdjustOverflow отключён, иначе на низком экране меню переворачивается
    // вверх и закрывает саму кнопку
    <Popover
      trigger='click'
      placement='bottomRight'
      autoAdjustOverflow={false}
      arrow={false}
      open={isMenuOpen}
      onOpenChange={onMenuOpenChange}
      overlayClassName={styles.mobileMenuPopover}
      overlayStyle={{ width: isMobile ? menuWidth : undefined }}
      content={
        <Menu
          mode='inline'
          items={menuLinks}
          selectedKeys={[current]}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
          onClick={onClick}
          className={styles.mobileMenu}
        />
      }
    >
      <Button
        ref={sectionButtonRef}
        size='large'
        block={isMobile}
        className={styles.sectionButton}
      >
        <span className={styles.sectionLabel}>{currentLabel}</span>
        <DownOutlined />
      </Button>
    </Popover>
  ) : (
    // Подменю в свёрнутом виде закрыты: их пункты без иконок, от них остались бы пустые строки
    <Menu
      mode='inline'
      items={menuLinks}
      selectedKeys={[current]}
      openKeys={isFolded ? [] : openKeys}
      onOpenChange={(keys) => setOpenKeys(keys as string[])}
      onClick={onClick}
      className={classNames('side-menu', styles.menu)}
    />
  )

  // У продавца переписка занимает весь экран: разделы открывают из таббара.
  // Админу так нельзя — он попал сюда из карточки продавца, и выйти ему больше нечем
  const isCompactChats =
    isMobile && type === 'profile' && current === adminRoutes.PROFILE_CHATS

  return (
    <div className={styles.root}>
      {type === 'seller' && (
        <MainTitle
          isGoBack
          text={`Продавец ID ${sellerNumberId ?? ''}${sellerName ? ` - ${sellerName}` : ''}`}
          onGoBack={ () => navigate(-1)}
        />
      )}
      <div
        className={classNames(styles.sideLayout, {
          [styles.floatingOffset]: type !== 'seller',
          [styles.sideLayoutCollapsible]: isCollapsible,
          [styles.sideLayoutCollapsed]: isSideCollapsed
        })}
      >
        <div
          className={classNames(styles.sideColumn, {
            [styles.sideColumnCollapsible]: isCollapsible,
            [styles.sideColumnCollapsed]: isSideCollapsed,
            [styles.sideColumnFolded]: isFolded,
            [styles.sideColumnPeek]: isSideCollapsed && isPeeking
          })}
          onMouseEnter={onSideMouseEnter}
          onMouseLeave={onSideMouseLeave}
        >
          {isCollapsible && (
            <button type='button' className={styles.collapseToggle} onClick={toggleCollapsed}>
              {isSideCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              <span className={styles.collapseLabel}>
                {isSideCollapsed ? 'Раскрыть меню' : 'Свернуть меню'}
              </span>
            </button>
          )}
          {!isCompactChats && sectionsMenu}
          <div id='seller-save-button-portal' className={styles.sideActions} />
        </div>
        <div className={classNames('side-content', styles.content)}>
          <Outlet />
        </div>
      </div>
    </div>
  )
}
